import PatternImage, { getPatternImageUrl } from "@/components/PatternImage";
import { graphql } from "@/gql";
import { useTokenStore } from "@/store/token";
import { useQuery } from "@/utils/graphql";
import { getUserIdFromJwt } from "@/utils/jwt";
import { normalizePatternData } from "@/utils/patternRenderer";
import { formatTime } from "@/utils/time";
import { DownloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Empty,
  Grid,
  Input,
  Space,
  Spin,
  Typography,
} from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const GET_PIXEL_ARTS = graphql(`
  query GetAllPixelArts(
    $limit: Int!
    $page: Int!
    $filter: PixelArtFilterInput!
  ) {
    pixelArts(
      pagination: { page: { limit: $limit, page: $page } }
      filters: $filter
      orderBy: { createdOn: DESC }
    ) {
      nodes {
        pixelArtId
        userId
        paletteName
        width
        height
        createdOn
        patternImageUrl
        patternGridJson
        patternLegendJson
        patternMetaJson
        user {
          id
          name
        }
      }
      paginationInfo {
        total
      }
    }
  }
`);

const PAGE_SIZE = 4;

export default function PlaygroundPage() {
  const { t } = useTranslation();
  const { accessToken } = useTokenStore();
  const screens = Grid.useBreakpoint();
  const [page, setPage] = useState(0);
  const [jumpPage, setJumpPage] = useState(0);

  const userId = useMemo(() => getUserIdFromJwt(accessToken), [accessToken]);

  const { data, error, isLoading, isFetching } = useQuery(GET_PIXEL_ARTS, {
    limit: PAGE_SIZE,
    page,
    filter: {},
  });

  const pixelArts = data?.pixelArts?.nodes || [];
  const total = data?.pixelArts?.paginationInfo?.total || 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleDownload = (art: (typeof pixelArts)[number]) => {
    const pattern = normalizePatternData({
      width: art.width,
      height: art.height,
      patternGridJson: art.patternGridJson,
      patternLegendJson: art.patternLegendJson,
      patternMetaJson: art.patternMetaJson,
    });
    const imageUrl = pattern
      ? getPatternImageUrl({
          ...pattern,
          title: `${art.width}×${art.height}`,
          fallbackUrl: art.patternImageUrl,
        })
      : art.patternImageUrl;
    if (!imageUrl) return;
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = `bead-pattern-${new Date().toISOString().slice(0, 10)}.png`;
    link.click();
  };

  if (!userId) {
    return <Empty description={t("common.loading")} />;
  }

  return (
    <div className="h-full min-h-0 space-y-6 overflow-y-auto overflow-x-hidden pr-1 pb-4">
      {/* Header */}
      <Card className="brand-surface-hero" variant="outlined">
        <div className="space-y-2">
          <Typography.Title level={2} className="m-0">
            {t("playground.title")}
          </Typography.Title>
          <Typography.Paragraph className="m-0 text-slate-600">
            {t("playground.subtitle")}
          </Typography.Paragraph>
        </div>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert
          type="error"
          showIcon
          title={`${t("common.error")}: ${error.message}`}
          closable
        />
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex min-h-64 items-center justify-center">
          <Spin size="large" description={t("playground.loading")} />
        </div>
      ) : pixelArts.length === 0 ? (
        <Card className="brand-surface-card" variant="outlined">
          <Empty
            description={t("playground.emptyState")}
            style={{ marginTop: 48, marginBottom: 48 }}
          />
        </Card>
      ) : (
        /* Gallery Grid */
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns:
              screens.md && screens.lg
                ? "repeat(auto-fill, minmax(300px, 1fr))"
                : screens.sm
                  ? "repeat(auto-fill, minmax(250px, 1fr))"
                  : "1fr",
          }}
        >
          {pixelArts.map((art) => (
            <Card
              key={art.pixelArtId}
              className="brand-surface-card hover:shadow-lg transition-shadow"
              variant="outlined"
              cover={
                art.patternImageUrl || art.patternGridJson ? (
                  <PatternImage
                    patternImageUrl={art.patternImageUrl}
                    patternGridJson={art.patternGridJson}
                    patternLegendJson={art.patternLegendJson}
                    patternMetaJson={art.patternMetaJson}
                    width={art.width}
                    height={art.height}
                    alt={`Healing art ${art.pixelArtId}`}
                    style={{
                      height: 250,
                      objectFit: "cover",
                      background: "#f5f5f5",
                    }}
                    previewMask={t("common.preview")}
                  />
                ) : (
                  <div
                    style={{
                      height: 250,
                      background: "#f5f5f5",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <span className="text-gray-400">{t("common.loading")}</span>
                  </div>
                )
              }
            >
              <Card.Meta
                title={`${art.width}×${art.height}`}
                description={
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500">
                      {t("playground.palette")}: {art.paletteName || "-"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t("playground.createdAt")}: {formatTime(art.createdOn)}
                    </div>
                  </div>
                }
              />
              <Space className="mt-3 w-full justify-end">
                {(art.patternImageUrl || art.patternGridJson) && (
                  <>
                    <Button
                      type="primary"
                      size="small"
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(art)}
                    >
                      {t("playground.downloadBtn")}
                    </Button>
                  </>
                )}
              </Space>
            </Card>
          ))}
        </div>
      )}
      {total > PAGE_SIZE ? (
        <div className="flex items-center justify-center gap-3">
          <Button
            disabled={page === 0 || isFetching}
            onClick={() => setPage(0)}
          >
            {t("common.start") || "Start"}
          </Button>
          <Button
            disabled={page === 0 || isFetching}
            onClick={() => setPage((value) => Math.max(0, value - 1))}
          >
            {t("common.previous") || "Previous"}
          </Button>
          <Typography.Text type="secondary">
            {page + 1} / {pageCount}
          </Typography.Text>
          <Input
            min={1}
            max={pageCount}
            defaultValue={page + 1}
            type="number"
            onChange={(event) => {
              const value = parseInt(event.target.value, 10);
              if (typeof value === "number") {
                setJumpPage(Math.min(pageCount - 1, Math.max(0, value - 1)));
              }
            }}
            style={{ width: 80 }}
          />
          <Button
            disabled={jumpPage === page || isFetching}
            onClick={() => setPage(jumpPage)}
          >
            {t("common.jump") || "Jump"}
          </Button>
          <Button
            disabled={page + 1 >= pageCount || isFetching}
            onClick={() =>
              setPage((value) => Math.min(pageCount - 1, value + 1))
            }
          >
            {t("common.next") || "Next"}
          </Button>
          <Button
            disabled={page + 1 >= pageCount || isFetching}
            onClick={() => setPage(pageCount - 1)}
          >
            {t("common.end") || "End"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
